import { imageUrl } from "@a2z/ui/media/image-url";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import type { Category, MenuItem } from "@/lib/api/types";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import {
  storedCategoryBadge,
  storedDishBadge,
} from "@/shared/doodles/categoryBadge";

/**
 * The PAPER look's register — the 2026-10 redesign's category chips and dish
 * tiles, drawn by `MenuBoard` while PAPER is the look on a light till. The
 * board keeps its own search, filtering, pricing and every tile control (86,
 * price override, swap); these draw only the chips and the tile's face.
 */

/**
 * A section's badge: the one the owner picked (`Category.badge`, founder
 * 2026-10-03), else the one read off its name. Shared by the chips and the
 * tiles so a section wears one badge everywhere on the register.
 */
export function sectionBadgeId(
  category: Pick<Category, "nameAr" | "nameEn" | "badge"> | null | undefined,
): DoodleBadgeId {
  return (
    storedCategoryBadge(category) ??
    categoryBadgeId(category ? `${category.nameAr} ${category.nameEn ?? ""}` : "")
  );
}

/**
 * The FALLBACK for a section with no stored badge — read off the name in both
 * languages; anything unrecognised is a plate and cutlery. This was the whole
 * rule before sections stored a badge, so a section nobody has touched still
 * looks exactly as it did.
 */
export function categoryBadgeId(name: string): DoodleBadgeId {
  const value = name.toLowerCase();
  const rules: Array<[RegExp, DoodleBadgeId]> = [
    [/drink|beverage|juice|coffee|tea|مشروب|عصير|قهوة|شاي|بارد|ساخن/, "mug"],
    [/dessert|sweet|cake|ice ?cream|حلو|حلويات|كيك|آيس|ايس/, "icecream"],
    [/pizza|بيتزا/, "pizza"],
    [/burger|sandwich|برجر|برغر|ساندوتش|سندوتش/, "burger"],
    [/pasta|noodle|ramen|مكرونة|معكرونة|نودلز/, "noodles"],
    [/soup|side|starter|appetizer|salad|شوربة|جانبي|مقبلات|سلطة/, "pot"],
    [/wine|bar|cocktail|نبيذ|بار/, "wine"],
    [/take ?away|box|meal|سفري|بوكس|وجبة/, "foodbag"],
    [/main|grill|chef|special|رئيسي|رئيسية|مشويات|شيف|خاص/, "cloche"],
  ];
  return rules.find(([pattern]) => pattern.test(value))?.[1] ?? "cutlery";
}

/**
 * The ground under a tile's 64px badge — the redesign's tinted well. The
 * design paints each section in a colour the owner picks; a section here has
 * no colour, only the badge the owner picked (or the one read off its name),
 * so the tint follows the badge: the design's own pairs (a burger on orange,
 * a pot on green, a drink on blue, a sundae on pink, a cloche on purple, a
 * food bag on yellow), and its grey for everything else.
 */
// Each well reads its dark tint from the PAPER palette under `.pos-dark`
// (`--pxc-ground-*`, `paper.css`); light leaves them unset and draws these.
const GROUND = {
  gray: "var(--pxc-ground-gray, rgb(241, 241, 239))",
  orange: "var(--pxc-ground-orange, rgb(251, 236, 221))",
  yellow: "var(--pxc-ground-yellow, rgb(253, 236, 200))",
  green: "var(--pxc-ground-green, rgb(219, 237, 219))",
  blue: "var(--pxc-ground-blue, rgb(211, 229, 239))",
  purple: "var(--pxc-ground-purple, rgb(232, 222, 238))",
  pink: "var(--pxc-ground-pink, rgb(245, 224, 233))",
  red: "var(--pxc-ground-red, rgb(255, 226, 221))",
} as const;
const BADGE_GROUND: Partial<Record<DoodleBadgeId, string>> = {
  burger: GROUND.orange,
  pizza: GROUND.orange,
  noodles: GROUND.orange,
  chef: GROUND.orange,
  pot: GROUND.green,
  takeaway: GROUND.blue,
  mug: GROUND.blue,
  icecream: GROUND.pink,
  cloche: GROUND.purple,
  wine: GROUND.red,
  foodbag: GROUND.yellow,
  star: GROUND.yellow,
  menu: GROUND.yellow,
  // The Restaurant set (2026-10-07), on the same pairs: drinks on blue,
  // sweets on pink, savoury mains on orange, greens and bowls on green,
  // bakery and breakfast on yellow, the sea on blue, the chilli on red.
  espresso: GROUND.blue,
  latte: GROUND.blue,
  "turkish-coffee": GROUND.blue,
  "iced-coffee": GROUND.blue,
  tea: GROUND.blue,
  "iced-tea": GROUND.blue,
  "hot-chocolate": GROUND.blue,
  juice: GROUND.blue,
  smoothie: GROUND.blue,
  lemonade: GROUND.blue,
  milkshake: GROUND.blue,
  mocktail: GROUND.blue,
  soda: GROUND.blue,
  sparkling: GROUND.blue,
  cake: GROUND.pink,
  donut: GROUND.pink,
  cookie: GROUND.pink,
  waffle: GROUND.pink,
  pancakes: GROUND.pink,
  combo: GROUND.orange,
  wrap: GROUND.orange,
  hotdog: GROUND.orange,
  taco: GROUND.orange,
  fries: GROUND.orange,
  pasta: GROUND.orange,
  grill: GROUND.orange,
  chicken: GROUND.orange,
  steak: GROUND.orange,
  kebab: GROUND.orange,
  falafel: GROUND.orange,
  soup: GROUND.green,
  salad: GROUND.green,
  veg: GROUND.green,
  rice: GROUND.green,
  bread: GROUND.yellow,
  croissant: GROUND.yellow,
  breakfast: GROUND.yellow,
  cheese: GROUND.yellow,
  seafood: GROUND.blue,
  shrimp: GROUND.blue,
  sushi: GROUND.blue,
  spicy: GROUND.red,
};
export function badgeGround(id: DoodleBadgeId): string {
  return BADGE_GROUND[id] ?? GROUND.gray;
}

/**
 * The tile that leads each step of the register — "Back · to Tables",
 * "Back · to Categories", "Back · Clear search". The design's grey card with
 * the library's back badge, mirrored in Arabic so the arrow points the way the
 * screen reads.
 */
export function PaperBackTile({
  sub,
  onClick,
  testId,
}: {
  sub: string;
  onClick: () => void;
  testId: string;
}) {
  const t = useTranslate();
  return (
    <button
      type="button"
      data-ui="dish-tile"
      data-kind="back"
      data-testid={testId}
      onClick={onClick}
      className="relative flex h-full w-full flex-col overflow-hidden text-start"
    >
      <span data-ui="dish-tile-media" className="flex items-center justify-center">
        <span data-ui="register-back-badge" className="flex rtl:-scale-x-100">
          <DoodleBadge id="back" size={64} animate={false} />
        </span>
      </span>
      <span data-ui="dish-tile-body" className="flex min-w-0 flex-col">
        <span data-ui="dish-tile-name" className="truncate">
          {t((m) => m.paperRegister.back)}
        </span>
        <span data-ui="dish-tile-price" className="truncate">
          {sub}
        </span>
      </span>
    </button>
  );
}

/**
 * A menu section on the register's first step: its badge on its tinted
 * ground, its name, how many dishes it holds — and, when any of them are on
 * this bill, the blue ring and the count in the corner, as a dish tile wears.
 */
export function PaperSectionTile({
  category,
  name,
  dishes,
  inOrder,
  onOpen,
}: {
  category: Category;
  name: string;
  /** The section's dishes, as the register would list them. */
  dishes: number;
  /** Plates of this section's dishes already on the bill. */
  inOrder: number;
  onOpen: () => void;
}) {
  const t = useTranslate();
  const badge = sectionBadgeId(category);
  return (
    <button
      type="button"
      data-ui="dish-tile"
      data-kind="section"
      data-in-order={inOrder > 0 ? "" : undefined}
      data-testid={`register-section-${category.id}`}
      onClick={onOpen}
      className="relative flex h-full w-full flex-col overflow-hidden text-start"
    >
      <span
        data-ui="dish-tile-media"
        className="flex items-center justify-center"
        style={{ background: badgeGround(badge) }}
      >
        <DoodleBadge id={badge} size={64} animate={false} />
      </span>
      <span data-ui="dish-tile-body" className="flex min-w-0 flex-col">
        <span data-ui="dish-tile-name" className="truncate">
          {name}
        </span>
        <span data-ui="dish-tile-price" className="truncate">
          {t(
            countedKey(dishes, {
              one: (m) => m.paperRegister.sectionDishesOne,
              two: (m) => m.paperRegister.sectionDishesTwo,
              few: (m) => m.paperRegister.sectionDishesFew,
              many: (m) => m.paperRegister.sectionDishes,
            }),
            { count: dishes },
          )}
        </span>
      </span>
      {inOrder > 0 ? (
        <span
          data-ui="dish-tile-count"
          className="absolute end-2 top-2 flex items-center justify-center tabular-nums"
        >
          {inOrder}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The tile's face: a 112px image well — the dish's photo, or its badge on its
 * section's tinted ground — then the name and one line under it: the price
 * ("from" it when the dish asks for a size or extras), or "Sold out" on a dish
 * the kitchen has run out of, which the tile also greys. The count of this
 * dish already on the bill sits in the corner. The page owns the button.
 */
export function PaperDishFace({
  item,
  name,
  price,
  inOrder,
  soldOut,
  soldOutLabel,
  ingredientOutLabel,
  extras,
  categories,
}: {
  item: MenuItem;
  name: string;
  /** The line under the name — the price, or "from" it. */
  price: string;
  inOrder: number;
  soldOut: boolean;
  soldOutLabel: string;
  /** Set when an ingredient in this dish is marked out (B18); the dish still sells. */
  ingredientOutLabel: string | null;
  /** A mark beside the price (a combo says so). */
  extras?: React.ReactNode;
  categories: Category[];
}) {
  const mediaBase = import.meta.env.VITE_MEDIA_BASE_URL;
  const category = categories.find((row) => item.categoryIds.includes(row.id));
  // The dish's own badge first (2026-10-03), then its section's.
  const sectionBadge = sectionBadgeId(category);
  const badge = storedDishBadge(item) ?? sectionBadge;
  return (
    <>
      <span
        data-ui="dish-tile-media"
        className="relative flex items-center justify-center overflow-hidden"
        style={{ background: badgeGround(sectionBadge) }}
      >
        {item.imageKey && mediaBase ? (
          <img
            src={imageUrl(
              item.imageKey,
              { width: 320, fit: "cover", format: "auto" },
              { baseUrl: mediaBase },
            )}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <DoodleBadge id={badge} size={64} animate={false} />
        )}
        {ingredientOutLabel && !soldOut ? (
          <span
            data-ui="dish-tile-flag"
            data-tone="warning"
            data-testid={`menu-item-ingredient-out-${item.id}`}
            className="absolute bottom-1.5 start-1.5 truncate"
          >
            {ingredientOutLabel}
          </span>
        ) : null}
      </span>
      <span data-ui="dish-tile-body" className="flex min-w-0 flex-col">
        <span data-ui="dish-tile-name" className="truncate">
          {name}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            data-ui="dish-tile-price"
            data-testid={soldOut ? `menu-item-sold-out-${item.id}` : undefined}
            className="whitespace-nowrap tabular-nums"
          >
            {soldOut ? soldOutLabel : price}
          </span>
          {soldOut ? null : extras}
        </span>
      </span>
      {!soldOut && inOrder > 0 ? (
        <span data-ui="dish-tile-count" className="absolute end-2 top-2 flex items-center justify-center tabular-nums">
          {inOrder}
        </span>
      ) : null}
    </>
  );
}

/**
 * The dish at the head of the PAPER add-on picker: its photo in a 56px
 * rounded square, or the plate-and-cutlery badge when it has none.
 */
export function PaperDishThumb({
  item,
  size = 56,
}: {
  item: MenuItem;
  /** The box: 56px at the picker's head, 28px on an order line. */
  size?: number;
}) {
  const mediaBase = import.meta.env.VITE_MEDIA_BASE_URL;
  return item.imageKey && mediaBase ? (
    <img
      data-ui="dish-thumb"
      src={imageUrl(
        item.imageKey,
        { width: size * 2, height: size * 2, fit: "cover", format: "auto" },
        { baseUrl: mediaBase },
      )}
      alt=""
      className="h-full w-full object-cover"
    />
  ) : (
    <DoodleBadge id={storedDishBadge(item) ?? "cutlery"} size={size} animate={false} />
  );
}

